import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Upload, Loader2, Eye } from "lucide-react";
import { api, fmtINR, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const STATUSES = ["booked", "confirmed", "in_progress", "completed", "cancelled"];

const payBadge = (p) => {
  const map = { paid: "bg-emerald-500/15 text-emerald-400", submitted: "bg-amber-500/15 text-amber-400", pending: "bg-zinc-500/15 text-zinc-600", rejected: "bg-red-500/15 text-red-400" };
  return map[p] || map.pending;
};

export default function Bookings() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [priceInput, setPriceInput] = useState("");

  const load = () => api.get("/admin/bookings").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/bookings/${id}`);
    setSel(data);
    setPriceInput(String(data.package_price_locked ?? ""));
  };

  const act = async (fn, msg) => {
    setLoading(true);
    try { await fn(); toast.success(msg); await load(); if (sel) await openDetail(sel.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const changeStatus = (id, status) => act(() => api.patch(`/admin/bookings/${id}/status`, { status }), "Status updated");
  const verify = (id) => act(() => api.post(`/admin/bookings/${id}/verify-payment`), "Payment verified & confirmed");
  const reject = (id) => act(() => api.post(`/admin/bookings/${id}/reject-payment`), "Payment rejected");
  const verifyBalance = (id) => act(() => api.post(`/admin/bookings/${id}/verify-balance`), "Balance verified");
  const rejectBalance = (id) => act(() => api.post(`/admin/bookings/${id}/reject-balance`), "Balance rejected");
  const markBalance = (id) => act(() => api.patch(`/admin/bookings/${id}/balance`), "Balance marked paid");
  const savePrice = (id) => act(() => api.patch(`/admin/bookings/${id}/price`, { price: Number(priceInput) }), "Price updated");
  const cancel = (id) => { if (window.confirm("Cancel booking? Refund = 75% of advance.")) act(() => api.post(`/admin/bookings/${id}/cancel`, {}), "Booking cancelled"); };

  const uploadPhoto = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    await act(() => api.post(`/admin/bookings/${id}/photos`, fd), "Photo uploaded");
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-zinc-900 mb-6">Bookings</h1>
      <div className="bg-card border border-pink-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pink-50 text-zinc-600 text-left">
              <tr>
                {["Booking #", "Customer", "Package", "Date", "Payment", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-t border-pink-100 hover:bg-pink-50/40" data-testid={`booking-row-${b.booking_number}`}>
                  <td className="px-4 py-3 text-gold font-mono whitespace-nowrap">{b.booking_number}</td>
                  <td className="px-4 py-3 text-zinc-900 whitespace-nowrap">{b.customer_name}<div className="text-xs text-zinc-500">{b.customer_phone}</div></td>
                  <td className="px-4 py-3 text-zinc-700 whitespace-nowrap">{b.package_name_en}</td>
                  <td className="px-4 py-3 text-zinc-700 whitespace-nowrap">{b.event_date}</td>
                  <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs ${payBadge(b.payment_status)}`}>{b.payment_status}</span></td>
                  <td className="px-4 py-3"><Badge variant="outline" className="border-pink-300 text-zinc-700 capitalize">{b.status.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(b.id)} data-testid={`manage-btn-${b.booking_number}`} className="border-pink-300 text-zinc-700"><Eye size={14} className="mr-1" /> Manage</Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No bookings yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-card border-pink-200 text-zinc-900 max-w-lg max-h-[90vh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{sel.booking_number}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Info label="Customer" value={sel.customer_name} />
                  <Info label="Phone" value={sel.customer_phone} />
                  <Info label="Email" value={sel.customer_email} />
                  <Info label="Event Date" value={sel.event_date} />
                  <Info label="Package" value={sel.package_name_en} />
                  <Info label="Price" value={fmtINR(sel.package_price_locked)} />
                  <Info label="Advance" value={fmtINR(sel.advance_amount)} />
                  <Info label="Balance" value={`${fmtINR(sel.balance_amount)}${sel.balance_paid ? " (paid)" : ""}`} />
                </div>
                {sel.address && <Info label="Address" value={sel.address} />}
                {sel.notes && <Info label="Notes" value={sel.notes} />}
                {sel.upi_ref && <Info label="UPI Reference" value={sel.upi_ref} />}

                {sel.payment_status === "submitted" && (
                  <div className="flex gap-2 p-3 rounded-lg bg-amber-500/10">
                    <Button size="sm" onClick={() => verify(sel.id)} disabled={loading} data-testid="verify-payment-btn" className="bg-emerald-500 hover:bg-emerald-600 text-black"><CheckCircle2 size={15} className="mr-1" /> Verify Payment</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(sel.id)} disabled={loading} className="border-red-500/40 text-red-400"><XCircle size={15} className="mr-1" /> Reject</Button>
                  </div>
                )}

                {sel.balance_payment_status === "submitted" && (
                  <div className="p-3 rounded-lg bg-amber-500/10 space-y-2">
                    <span className="text-xs text-zinc-600 block">Balance UPI Ref: <b className="text-zinc-900">{sel.balance_upi_ref}</b></span>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => verifyBalance(sel.id)} disabled={loading} data-testid="verify-balance-btn" className="bg-emerald-500 hover:bg-emerald-600 text-black"><CheckCircle2 size={15} className="mr-1" /> Verify Balance</Button>
                      <Button size="sm" variant="outline" onClick={() => rejectBalance(sel.id)} disabled={loading} className="border-red-500/40 text-red-400"><XCircle size={15} className="mr-1" /> Reject</Button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs uppercase tracking-wide text-zinc-500">Update Status</label>
                  <Select value={STATUSES.includes(sel.status) ? sel.status : undefined} onValueChange={(v) => changeStatus(sel.id, v)}>
                    <SelectTrigger data-testid="status-select" className="mt-1 bg-white border-pink-200"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wide text-zinc-500 block mb-1">Custom Price (₹){sel.custom_price ? " · edited" : ""}</label>
                  <div className="flex gap-2">
                    <Input type="number" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} data-testid="booking-price-input" className="bg-white border-pink-200 h-9" />
                    <Button size="sm" onClick={() => savePrice(sel.id)} disabled={loading || priceInput === ""} data-testid="save-price-btn" className="gold-gradient text-white font-semibold whitespace-nowrap">Save Price</Button>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1">Balance auto-recalculates (price − advance paid).</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!sel.balance_paid && <Button size="sm" variant="outline" onClick={() => markBalance(sel.id)} disabled={loading} className="border-pink-300 text-zinc-700">Mark Balance Paid</Button>}
                  {sel.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => cancel(sel.id)} disabled={loading} className="border-red-500/40 text-red-400">Cancel Booking</Button>}
                  {sel.refund_amount > 0 && <span className="text-xs text-red-400 self-center">Refund: {fmtINR(sel.refund_amount)}</span>}
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wide text-zinc-500 block mb-2">Event Photos</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(sel.photos || []).map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="h-16 w-16 object-cover rounded-lg border border-pink-200" />)}
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gold cursor-pointer" data-testid="upload-photo-label">
                    <Upload size={15} /> Upload photo
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(sel.id, e.target.files[0])} />
                  </label>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-zinc-900 break-words">{value}</div>
    </div>
  );
}
